/**
 * What both sides of a credential reminder have to agree about.
 *
 * PRD §3.16's last sentence: "Last-used and last-rotated timestamps surface in
 * Settings with **rotation reminders**." Nothing states the age, and the only
 * constraint is from the Visual Reference — `settings-credentials.html` flags a
 * key last rotated ELEVEN MONTHS ago, so the threshold is at most eleven
 * months. Q-062, answered by DECISION-086.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS IN `packages/schema` AND NOT IN THE COMPONENT THAT DRAWS IT
 * ────────────────────────────────────────────────────────────────────────────
 * TWO surfaces read it and they are in different packages. The settings screen
 * draws the `◐` glyph and the "Last rotated …" line; the needs-attention feed
 * is composed SERVER-SIDE — `docs/api-contract.md` lists `credentials`
 * (rotation due) among its six sources and returns the whole feed from
 * `GET /v1/w/{wid}/dashboard` in one call. The dependency direction is
 * `schema ← api ← app`, so the API cannot import the app's constant and this is
 * the only place both can reach.
 *
 * THE PRECEDENT IS A LIVE DUPLICATION, WHICH IS THE POINT. `USAGE_THRESHOLDS`
 * is `[0.8, 0.95]` in `services/billing/usage.ts` and the same two numbers are
 * written again as literals in `pages/settings/Billing.tsx` — whose own comment
 * says why they must agree: "the email and the bar say the same thing at the
 * same moment." Nothing makes them. Changing one silently stops the meter
 * matching the email that fires with it. This file exists so the rotation
 * reminder does not become the second instance of that.
 *
 * NOT `config`, and RULE-042 is the reason rather than an exception to it. That
 * rule keeps MODEL, Neuron and PLAN-LIMIT values in `config` because they must
 * change without a deploy — they are tied to vendors and to money. A rotation
 * cadence is neither; changing it is an ordinary code change, and a D1 row for
 * it would be a migration and a read on every dashboard for a number that moves
 * once a decade.
 *
 * Story 11.2 / DECISION-086.
 */

/**
 * How old a credential's current value may get before Leadgible mentions it.
 *
 * 180 days. DERIVED, not invented: the Visual Reference flags an eleven-month-
 * old key, which caps it, and six months is the cadence the providers of
 * secrets publish for their own keys.
 *
 * IT IS A REMINDER AND NOTHING ELSE. Nothing is disabled, no key stops working,
 * no email is sent, and no request is refused because of it. That is what makes
 * a wrong threshold cheap — a sentence appearing early or late on one screen
 * and one feed row — and it is the property to preserve if the number ever
 * changes. The moment something is WITHHELD at this boundary it stops being a
 * reminder and needs a different conversation.
 */
export const ROTATION_REMINDER_DAYS = 180

const DAY_MS = 86_400_000

/**
 * When the credential's value last changed, if it is old enough to say so.
 *
 * Returns the timestamp rather than a boolean because both callers render the
 * AGE — "Last rotated 11 months ago" — and a boolean would make each of them
 * work out which of the two dates it came from a second time.
 *
 * `lastRotatedAt` is NULL until the first replace, so the fallback is
 * `createdAt`: the age of the SECRET is what a rotation reminder is about, and
 * a key that has never been rotated has been the same key since it was added.
 */
export function rotationDueAt(
  credential: { lastRotatedAt: number | null; createdAt: number },
  now: number,
): number | null {
  const changed = credential.lastRotatedAt ?? credential.createdAt
  return now - changed >= ROTATION_REMINDER_DAYS * DAY_MS ? changed : null
}

/* ── what to call a provider ────────────────────────────────────────────── */

/**
 * The display name for a stored `credentials.provider` value.
 *
 * SAME REASON AS THE THRESHOLD ABOVE, one story later. `credentials.provider`
 * holds an id — `openai`, `mailgun` — and Story 11.1's registry deliberately
 * carries no display names, because "a provider entry is a tester plus its
 * fields, and a registry that also knew what to call things would be what every
 * later feature reached into". So the labels lived in the one screen that
 * needed them, `pages/settings/Credentials.tsx`.
 *
 * Story 12.2 is the SECOND reader and it is in `packages/api`: the
 * needs-attention feed composes "Your OpenAI key hasn't been rotated in 11
 * months" server-side, and `schema ← api ← app` means the API cannot reach the
 * component. Without this it would write `Your openai key`, which is the raw
 * column value — the same defect `docs/BACKLOG.md` already records against the
 * audit summariser's `Ariel Kessler connected openai.`
 *
 * FIVE, NOT SIX: the mail provider is deliberately absent. Story 11.1 keeps the
 * vendor's name off the seam — `credentials.provider` is literally `mailgun`
 * but every reader takes it off the adapter as `EMAIL_PROVIDER_NAME`, so the
 * boundary scan stays green with no new exemption. `providerLabel` falls back
 * to the id, which is what a caller with a provider this list does not name
 * should show.
 *
 * Story 12.2 / DECISION-086's placement, applied a second time.
 */
export const CREDENTIAL_PROVIDERS = [
  { id: 'openai', label: 'OpenAI', baseUrl: 'optional' },
  { id: 'azure', label: 'Azure OpenAI', baseUrl: 'required' },
  { id: 'openrouter', label: 'OpenRouter', baseUrl: 'optional' },
  { id: 'groq', label: 'Groq', baseUrl: 'optional' },
  { id: 'other', label: 'Other', baseUrl: 'required' },
] as const

/**
 * The label, or the id itself.
 *
 * The fallback is the id rather than a placeholder for the reason `bandsById`
 * falls back to a band's id: it is at least a string somebody chose, where
 * "Unknown provider" is a word nobody did.
 */
export function providerLabel(id: string): string {
  return CREDENTIAL_PROVIDERS.find((p) => p.id === id)?.label ?? id
}
